import { computed, makeObservable, observable } from 'mobx'
import { combineLatest, Subscription } from 'rxjs'
import { SearchableUser } from 'iam/models/SearchableUser'
import { UserDataType } from 'iam/models/UserDataType'
import Workspace from 'iam/models/Workspace'
import SearchableUserStore from 'iam/SearchableUserStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import {
  FilterItemSource,
  FilterStorageFactory,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { switchMap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { tapAction } from 'utils/mobx-util'

type ClientUserFilter = {
  inactive?: boolean | null
}

type ClientUserSearchFilter = ClientUserFilter & { searchText?: string }

export class ClientUserSearchViewModel {
  private subscription = new Subscription()

  searchText = new SearchTextViewModel(500)

  filterStorage = this.createFilterStorage('ClientUserManagementTableFilters')

  @observable
  totalUsers = 0

  @observable
  workspaceId: Id = Id.none

  filters = new FiltersViewModel<ClientUserFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      inactive: {
        icon: 'pending',
        title: 'Inactive',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            false,
            FilterItemSource.static([]),
          ),
      },
    },
  })

  scrollProvider = new InfiniteScrollProvider<
    SearchableUser,
    ClientUserSearchFilter
  >({
    limit: 100,
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this.store.fetchUsers({
        search_text: filter?.searchText,
        limit: paging.limit,
        after: paging.after,
        workspace_id: this.workspaceId,
        user_types: [UserDataType.User],
        inactive: filter?.inactive,
        role_id: undefined,
      }),
  })

  private query$ = combineLatest([
    this.filters.appliedFilters$,
    this.searchText.debouncedValue$,
  ]).pipe(
    switchMap(([appliedFilters, searchText]) =>
      this.scrollProvider.fetch({ searchText, ...appliedFilters }),
    ),
    tapAction((result) => (this.totalUsers = result!.total!)),
  )

  constructor(
    private store: SearchableUserStore,
    private getCurrentMember: GetCurrentMember,
    private createFilterStorage: FilterStorageFactory,
  ) {
    makeObservable(this)
  }

  @computed
  get loadedUsersCount() {
    return this.scrollProvider.items.length
  }

  @computed
  get userInactiveFilter() {
    return this.filters.getFilter('inactive') as SingleSelectFilter<
      string,
      boolean
    >
  }

  activate() {
    const currentSessionWorkspace =
      this.getCurrentMember().workspace ?? Workspace.none
    this.workspaceId = currentSessionWorkspace.id
    this.filters.activate()
    this.scrollProvider.fetch()
    this.subscription = this.query$.subscribe()
  }

  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }
}
